import { DollarSign, TrendingUp, BarChart3, Calendar } from "lucide-react";

interface SalepageRevenueProps {
  stats: any;
  allPayments: any[];
  formatCurrency: (amount: number) => string;
  getStatusBadge: (status: string) => JSX.Element;
}

export default function SalepageRevenue({ stats, allPayments, formatCurrency, getStatusBadge }: SalepageRevenueProps) {
  const sortedPayments = [...allPayments].sort((a, b) => {
    const aTime = a.createdAt?.toDate()?.getTime() || 0;
    const bTime = b.createdAt?.toDate()?.getTime() || 0;
    return bTime - aTime;
  });

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">รายได้ทั้งหมด</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">รายได้เดือนนี้</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenueThisMonth)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">MRR</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.mrr)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-emerald-600" />
          ประวัติการชำระเงิน ({allPayments.length})
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">วันที่</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">จำนวนเงิน</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">สถานะ</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">ผู้ใช้</th>
              </tr>
            </thead>
            <tbody>
              {sortedPayments.length > 0 ? (
                sortedPayments.map((payment, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-600">
                      {payment.createdAt?.toDate().toLocaleDateString('th-TH', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {payment.userEmail || payment.userId}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-gray-500">
                    ยังไม่มีการชำระเงิน
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
